import React from 'react';
import Accordion, { AccordionItemProps } from 'components/commercetools-ui/molecules/Accordion/Accordion';
import { Product } from 'shared/types/product/Product';
import {
  PDPSpecificationConfiguration,
  Section as SpecificationSection,
} from 'shared/types/query/PDPSpecificationConfiguration';
import { Variant } from 'shared/types/product';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useProjectSettings } from 'frontastic';
import ProductSpecification from './ProductSpecification';

const TrustBox = dynamic(() => import('components/commercetools-ui/organisms/trustbox-reviews'), { ssr: false });

interface ProductInformationAccordionProps {
  product?: Product;
  selectedVariant?: Variant;
}

const ProductInformationAccordion = ({ product, selectedVariant }: ProductInformationAccordionProps) => {
  const { data: config } = useProjectSettings();
  const pdpSpecificationConfiguration = JSON.parse(
    config?.pdpSpecificationConfiguration ?? '{}',
  ) as PDPSpecificationConfiguration;
  const currentSpecification: SpecificationSection[] = product?.productTypeId
    ? (pdpSpecificationConfiguration.specifications?.[product.productTypeId] ?? [])
    : [];
  const shouldShowProp65Warning = !!selectedVariant?.attributes?.prop65CaResidents;

  if (!product) return null;

  const Trustbox: React.FC<{ productSku: string }> = ({ productSku }) => {
    return <TrustBox productSku={productSku} />;
  };

  const accordionItems: AccordionItemProps[] = [
    {
      title: 'Description',
      content: (
        <div className="mt-8 max-w-none" data-testid="accordion-product-description">
          <p>{product.description}</p>
        </div>
      ),
      isDefaultOpen: true,
    },
    ...(!!currentSpecification.length
      ? [
          {
            title: 'Specifications',
            content: (
              <ProductSpecification
                specificationSections={currentSpecification}
                selectedVariant={selectedVariant}
                shouldShowProp65Warning={shouldShowProp65Warning}
              />
            ),
            isDefaultOpen: true,
          },
        ]
      : []),
    {
      title: 'Reviews',
      titleContent: (
        <div className="relative">
          <div className="left-0 top-0 mx-auto mt-8 flex h-[65px] w-[300px] overflow-hidden lg:absolute lg:mt-0 lg:-translate-x-1/2 lg:-translate-y-1/2">
            <Trustbox productSku={(selectedVariant?.sku || '').replace(/\s/g, '')} />
          </div>
        </div>
      ),
      content: (
        <div className="flex flex-col gap-3">
          <div className="mt-8 h-[500px] max-w-none overflow-hidden">
            <Trustbox productSku={(selectedVariant?.sku || '').replace(/\s/g, '')} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full pb-16 pt-8">
      <Accordion items={accordionItems} />
    </div>
  );
};

export default ProductInformationAccordion;
